import * as React from "react";
import {ThreeDots} from "@dvrt/graphql-widgets";
import {labelStyle, valueStyle} from "../../styles";
import BackendError from "../BackendError";
import {Grid, Stack, useTheme} from "@mui/material";
import Section from "../Section";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";
import EnabledOrDisabledItem from "./EnabledOrDisabledItem";
import {useLimitsSummaryQuery, useLimitsSummarySuperuserQuery} from "./queries.generated";
import {Page} from "../pages";
import {useAuth} from "@/context/AuthProvider";
import {SERVICE_NAME_MESSAGES} from "@/utils/translations";
import {LimitsQuota} from "@/api";
import QuotaTable from "./QuotaTable";

function NotSet() {
    const theme = useTheme();
    return (
        <span style={{fontStyle: "italic", color: theme.palette.text.secondary}}>
            <FormattedMessage id="notSet" defaultMessage="Not set" />
        </span>
    );
}

/**
 * Returns a function that generates MessageDescriptors for item names with the given prefix.
 * Used to create localized labels for quota items.
 **/
const getItemLabeler =
    (prefix: string) =>
    (name: string): MessageDescriptor => {
        // eslint-disable-next-line formatjs/enforce-id
        return defineMessage({
            id: `${prefix}.${name}`,
            // eslint-disable-next-line formatjs/enforce-default-message
            defaultMessage: name,
        });
    };

type QuotaTableSectionProps = {
    sectionLabel: MessageDescriptor;
    itemLabelFor: (name: string) => MessageDescriptor;
    items: LimitsQuota[];
};

const QuotaTableSection: React.FC<QuotaTableSectionProps> = ({sectionLabel, itemLabelFor, items}) => {
    return (
        <Section label={sectionLabel}>
            <QuotaTable
                items={items.map((item) => ({
                    ...item,
                    label: itemLabelFor(item.name),
                }))}
            />
        </Section>
    );
};

export default function LimitSummaryPage() {
    const auth = useAuth();
    const isSuperuser = auth.hasPermission("edit_system_settings");
    const {loading, error, data} = isSuperuser ? useLimitsSummarySuperuserQuery() : useLimitsSummaryQuery();

    if (loading) return <ThreeDots height={5} />;

    if (error) return <BackendError error={error} />;

    return (
        <Page title={defineMessage({id: "limitsSummary.title", defaultMessage: "Limits summary"})}>
            <Stack spacing={3}>
                <Section label={defineMessage({id: "validity", defaultMessage: "Validity"})}>
                    <Grid container>
                        <Grid style={labelStyle} size="grow">
                            <FormattedMessage id="issuedFor" defaultMessage="Issued for" />:
                        </Grid>
                        <Grid style={valueStyle} size="grow">
                            {data.limitsSummary.validity.issuedFor}
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid style={labelStyle} size="grow">
                            <FormattedMessage id="hostname" defaultMessage="Hostname" />:
                        </Grid>
                        <Grid style={valueStyle} size="grow">
                            {data.limitsSummary.validity.hostname?.join(", ") ?? <NotSet />}
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid style={labelStyle} size="grow">
                            <FormattedMessage id="expirationDate" defaultMessage="Expiration date" />:
                        </Grid>
                        <Grid style={valueStyle} size="grow">
                            {data.limitsSummary.validity.expirationDate ?? <NotSet />}
                        </Grid>
                    </Grid>
                </Section>
                <Section label={defineMessage({id: "features", defaultMessage: "Features"})}>
                    <EnabledOrDisabledItem
                        label={defineMessage({id: "billing", defaultMessage: "Billing"})}
                        value={data.limitsSummary.features.billing}
                    />
                    <EnabledOrDisabledItem
                        label={defineMessage({id: "call_queue.label", defaultMessage: "Call queue"})}
                        value={data.limitsSummary.features.callQueue}
                    />
                    <EnabledOrDisabledItem
                        label={defineMessage({id: "sip_uris", defaultMessage: "SIP URIs"})}
                        value={data.limitsSummary.features.sipUris}
                    />
                </Section>
                <QuotaTableSection
                    sectionLabel={defineMessage({id: "capability_sets", defaultMessage: "Capability sets"})}
                    itemLabelFor={getItemLabeler("capability_set_names")}
                    items={data.limitsSummary.capabilitySets}
                />
                <QuotaTableSection
                    sectionLabel={defineMessage({id: "device_type_sets", defaultMessage: "Device type sets"})}
                    itemLabelFor={getItemLabeler("device_type_set_names")}
                    items={data.limitsSummary.deviceTypeSets}
                />
                <QuotaTableSection
                    sectionLabel={defineMessage({id: "limits_packages", defaultMessage: "Packages"})}
                    itemLabelFor={getItemLabeler("user_package_names")}
                    items={data.limitsSummary.packages}
                />
                <QuotaTableSection
                    sectionLabel={defineMessage({id: "services", defaultMessage: "Services"})}
                    itemLabelFor={(name: string) => SERVICE_NAME_MESSAGES[name as keyof typeof SERVICE_NAME_MESSAGES]}
                    items={data.limitsSummary.services}
                />
            </Stack>
        </Page>
    );
}
